import type { UIPartyCharacter } from '@sorrem-client/types';
import type { ComponentProps } from 'react';
import type { CharacterCreationDraft } from '@sorrem-client/ui/features/CharacterHome/hooks';
import type { CharacterHomeViewport } from '../CharacterHomeViewport';

/** Who the home viewport shows, and how. */
export type CharacterHomeViewportShot = Pick<
    ComponentProps<typeof CharacterHomeViewport>,
    'characterId' | 'equippedItemIds' | 'outfitModifiers' | 'itemTiers' | 'isCreating' | 'animationsEnabled'
>;

interface ViewportPropsInput {
    /** A character is being created: the viewport previews the draft. */
    readonly isCreationViewShown: boolean;
    readonly draft: Pick<CharacterCreationDraft, 'selectedBaseModel' | 'equippedItemIds' | 'outfitModifiers'>;
    readonly player: Pick<UIPartyCharacter, 'characterModelId' | 'modelAttachmentItemIds' | 'modelOutfitModifiers' | 'modelItemTiers'> | null;
    /** Whether the 3D home moves: the account's "Character Home effects" is All. */
    readonly animationsEnabled: boolean;
    /** The home is hidden behind the Skills tab's sky and the Journey's table; it doesn't animate there. */
    readonly isHomeHidden: boolean;
}

/** The home viewport's character: the creation draft while creating one, else the selected character. */
export function resolveViewportProps({ isCreationViewShown, draft, player, animationsEnabled, isHomeHidden }: ViewportPropsInput): CharacterHomeViewportShot {
    return isCreationViewShown
        ? {
            characterId: draft.selectedBaseModel,
            equippedItemIds: draft.equippedItemIds,
            outfitModifiers: draft.outfitModifiers,
            isCreating: true,
            animationsEnabled,
        }
        : {
            characterId: player?.characterModelId ?? '',
            equippedItemIds: player?.modelAttachmentItemIds ?? [],
            outfitModifiers: player?.modelOutfitModifiers,
            itemTiers: player?.modelItemTiers,
            isCreating: false,
            animationsEnabled: animationsEnabled && !isHomeHidden,
        };
}
